/**
 * @fileoverview Shared user-facing pipeline status copy for Gist creation waits.
 *
 * WHY THIS EXISTS
 * - /try result cards, share SSR, URL-prefix shells, and the unified reader
 *   preparing affordance must show the same labels while work is in flight.
 * - A single source of truth prevents drift between ingest UI and reader UI.
 *
 * ARCHITECTURE ROLE
 * - Source of truth: this file.
 * - Browser reader presenter imports via `../../shared/gist-pipeline-status.js`.
 * - Backend Worker and /try client import via
 *   `@gist-protocol/core/shared/gist-pipeline-status.js`.
 *
 * @file shared/gist-pipeline-status.js
 */

/**
 * Fixed labels for scrape / ingest phases before generation cycling begins.
 *
 * @type {Readonly<{ LOADING: string; REAL_BROWSER: string }>}
 */
export const GistPipelineStatusLabels = Object.freeze({
  /** Normal URL scrape and generic ingest/upload waits. */
  LOADING: 'Loading',
  /** Remote-browser fallback after the fast scrape path is insufficient. */
  REAL_BROWSER: 'Trying a real browser…'
});

/**
 * Rotating labels shown while the layered Gist is being produced.
 *
 * @type {readonly string[]}
 */
export const GIST_PIPELINE_CYCLE_LABELS = Object.freeze([
  'Reading',
  'Gisting',
  'Structuring',
  'Layering',
  'Rebutting',
  'Steelmanning'
]);

/** First label in {@link GIST_PIPELINE_CYCLE_LABELS} for SSR first paint. */
export const GIST_PIPELINE_CYCLE_INITIAL_LABEL = GIST_PIPELINE_CYCLE_LABELS[0];

/** Interval between pipeline cycle label advances in the preparing affordance. */
export const GIST_PIPELINE_LABEL_CADENCE_MS = 2800;

/** Opacity fade duration when swapping cycle labels. */
export const GIST_PIPELINE_LABEL_FADE_MS = 220;
